'use client';

import { Button } from '@ahafood/ui/components/button';
import { cn } from '@ahm/common-helpers';
import { format, addDays, startOfDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Calendar } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { DATE_RANGE_OPTIONS, useAIPlannerStore } from '@/store/ai-planner';

export function StepDateRange() {
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const nextStep = useAIPlannerStore.use.nextStep();

  const [selectedRange, setSelectedRange] = useState<number | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  // const startDate = useMemo(() => {
  //   return preferences.startDate ? new Date(preferences.startDate) : today;
  // }, [preferences.startDate, today]);

  const endDate = useMemo(() => {
    return preferences.endDate ? new Date(preferences.endDate) : null;
  }, [preferences.endDate]);

  const handleQuickSelect = useCallback(
    (weeks: number, days: number) => {
      setSelectedRange(weeks);
      const start = today;
      const end = addDays(start, days - 1);
      setPreferences({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
    },
    [today, setPreferences]
  );

  const handleStartDateChange = useCallback(
    (date: Date) => {
      setSelectedRange(null);
      setPreferences({ startDate: date.toISOString() });
      // Auto-calculate end date if duration is set
      if (endDate && preferences.startDate) {
        const currentDuration = Math.ceil(
          (new Date(preferences.endDate!).getTime() -
            new Date(preferences.startDate).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        setPreferences({
          startDate: date.toISOString(),
          endDate: addDays(date, currentDuration).toISOString(),
        });
      }
    },
    [endDate, preferences, setPreferences]
  );

  const canProceed = preferences.startDate && preferences.endDate;

  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: vi });
  };

  return (
    <div className="flex flex-col h-full min-h-[400px]">
      {/* Start Date */}
      <div className="mb-4">
        <label className="text-white/60 text-sm mb-2 block">
          Thời gian bắt đầu kế hoạch *
        </label>
        <button
          className="w-full flex items-center justify-between bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
          onClick={() => {
            // In a real implementation, this would open a date picker
            // For now, we'll just use today
            handleStartDateChange(today);
          }}
        >
          <span>
            {preferences.startDate
              ? `${formatDate(new Date(preferences.startDate))} (hôm nay)`
              : 'Chọn ngày bắt đầu'}
          </span>
          <Calendar className="size-5 text-white/40" />
        </button>
      </div>

      {/* End Date */}
      <div className="mb-4">
        <label className="text-white/60 text-sm mb-2 block">
          Thời gian kết thúc kế hoạch *
        </label>
        <button
          className="w-full flex items-center justify-between bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white"
          disabled={!preferences.startDate}
        >
          <span className={!endDate ? 'text-white/40' : ''}>
            {endDate ? formatDate(endDate) : 'Chọn ngày kết thúc'}
          </span>
          <Calendar className="size-5 text-white/40" />
        </button>
      </div>

      {/* Quick Select */}
      <div className="flex gap-2 mb-4">
        {DATE_RANGE_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleQuickSelect(option.value, option.days)}
            className={cn(
              'flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all',
              'border',
              selectedRange === option.value
                ? 'bg-[#ff8200] border-[#ff8200] text-white'
                : 'bg-transparent border-white/20 text-white hover:border-[#ff8200]/50'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Info Text */}
      <p className="text-white/40 text-xs mb-6">
        Tuần đầu sẽ bạn gồm cơ đầu tiên phù để ước nào bạn cài đặt tuần đầu tiên
        dần để tính toán hợp lý.{' '}
        <span className="text-[#ff8200]">
          Kế hoạch kéo dài từ 1-3 tuần và có thể tái tạo mỗi tuần.
        </span>
      </p>

      {/* Spacer */}
      <div className="flex-1" />

      {/* CTA Button */}
      <Button
        onClick={nextStep}
        disabled={!canProceed}
        className={cn(
          'w-full rounded-full py-6 font-semibold',
          'bg-[#ff8200] hover:bg-[#ff8200]/90 text-white',
          'flex items-center justify-center gap-2',
          !canProceed && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="text-lg">✨</span>
        Xem trước thực đơn với AI
      </Button>
    </div>
  );
}
